// React component

import { motion, AnimatePresence } from "framer-motion";
import {
  Activity,
  Bell,
  Clock,
  Users,
  Zap,
  ArrowUpDown,
  Globe,
  Wifi,
} from "lucide-react";
import { useState, useEffect } from "react";

const RealTimeUpdates = () => {
  const [currentTime, setCurrentTime] = useState<Date | null>(null);
  const [activeUsers, setActiveUsers] = useState(150);
  const [notifications, setNotifications] = useState(3);
  const [dataPoints, setDataPoints] = useState(
    Array.from({ length: 8 }, () => 40),
  );
  const [syncStatus, setSyncStatus] = useState(0);

  useEffect(() => {
    // Set initial time and randomize data on client side only to prevent hydration mismatch
    setCurrentTime(new Date());
    setActiveUsers(Math.floor(Math.random() * 100) + 150);
    setNotifications(Math.floor(Math.random() * 5) + 3);
    setDataPoints(Array.from({ length: 8 }, () => Math.random() * 60 + 20));

    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    const userTimer = setInterval(() => {
      setActiveUsers((prev) => prev + Math.floor(Math.random() * 3) - 1);
    }, 2000);

    const notificationTimer = setInterval(() => {
      setNotifications((prev) => Math.min(prev + 1, 10));
      setTimeout(() => {
        setNotifications((prev) => Math.max(prev - 1, 3));
      }, 1000);
    }, 5000);

    const dataTimer = setInterval(() => {
      setDataPoints((prev) => [...prev.slice(1), Math.random() * 60 + 20]);
    }, 1000);

    const syncTimer = setInterval(() => {
      setSyncStatus((prev) => (prev + 1) % 100);
    }, 50);

    return () => {
      clearInterval(timer);
      clearInterval(userTimer);
      clearInterval(notificationTimer);
      clearInterval(dataTimer);
      clearInterval(syncTimer);
    };
  }, []);

  return (
    <div
      className="w-full h-full relative bg-background/50 rounded-xl p-4 overflow-hidden"
      data-oid="_edqf-b"
    >
      {/* Animated background gradient */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-pink-500/10"
        animate={{
          backgroundPosition: ["0% 0%", "100% 100%"],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          repeatType: "reverse",
        }}
        data-oid="ljaqf:p"
      />

      {/* Floating particles - reduced number for better performance */}
      {Array.from({ length: 10 }).map((_, i) => (
        <motion.div
          key={i}
          className="absolute h-1 w-1 rounded-full bg-blue-500/20"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.2, 0.5, 0.2],
          }}
          transition={{
            duration: 2 + Math.random() * 2,
            repeat: Infinity,
            delay: Math.random() * 2,
          }}
          data-oid="wlav2g7"
        />
      ))}

      {/* Content */}
      <div
        className="relative z-10 flex flex-col h-full gap-3"
        data-oid="7ocqx51"
      >
        {/* Header with time and status */}
        <div
          className="flex items-center justify-between mb-1"
          data-oid="dkd:8ea"
        >
          <motion.div
            className="flex items-center gap-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            data-oid="gp.lyr:"
          >
            <Clock className="h-4 w-4 text-blue-500" data-oid="y4r-5up" />
            <span className="text-sm font-medium" data-oid="el4r:q6">
              {currentTime ? currentTime.toLocaleTimeString() : "--:--:--"}
            </span>
          </motion.div>
          <motion.div
            className="flex items-center gap-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            data-oid="85evswe"
          >
            <Wifi className="h-4 w-4 text-green-500" data-oid="k7i9:qq" />
            <span className="text-xs text-green-500" data-oid=".6my.cg">
              Live
            </span>
          </motion.div>
        </div>

        {/* Real-time Graph - main focus */}
        <motion.div
          className="bg-card/30 backdrop-blur-sm p-3 rounded-lg border border-border relative overflow-hidden"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          data-oid="q8f7j8l"
        >
          <div
            className="flex items-center justify-between mb-2"
            data-oid="khp5fmk"
          >
            <div className="flex items-center gap-2" data-oid="go4vo.l">
              <Activity className="h-4 w-4 text-green-500" data-oid="cln4e.c" />
              <span className="text-sm font-medium" data-oid="wpdef_7">
                Real-time Activity
              </span>
            </div>
          </div>

          <div className="h-[100px] flex items-end gap-1" data-oid="onxjtbp">
            {dataPoints.map((point, index) => (
              <motion.div
                key={index}
                className="flex-1 bg-blue-500/20 rounded-t-sm relative group"
                style={{ height: `${point}%` }}
                initial={{ height: 0 }}
                animate={{ height: `${point}%` }}
                transition={{ duration: 0.5 }}
                data-oid="nz45nfa"
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-t from-blue-500/30 to-purple-500/30"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  data-oid="_mts9:v"
                />

                <div
                  className="absolute -top-6 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-card p-1 rounded text-xs whitespace-nowrap"
                  data-oid="9726a.s"
                >
                  {Math.round(point)}%
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Stats Grid - more compact */}
        <div className="grid grid-cols-2 gap-3" data-oid="m50x:e.">
          {/* Left column */}
          <div className="space-y-3" data-oid="p4arwi1">
            {/* Active Users */}
            <motion.div
              className="bg-card/30 backdrop-blur-sm p-2 rounded-lg border border-border"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              data-oid="fllb6yr"
            >
              <div className="flex items-center gap-2 mb-1" data-oid="bijlj::">
                <Users className="h-4 w-4 text-purple-500" data-oid="ysml-pu" />
                <span className="text-xs font-medium" data-oid="5-3c7o8">
                  Active Users
                </span>
              </div>
              <motion.div
                key={activeUsers}
                className="text-sm font-bold"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                data-oid="tlbtxbb"
              >
                {activeUsers}
              </motion.div>
            </motion.div>

            {/* Global Sync */}
            <motion.div
              className="bg-card/30 backdrop-blur-sm p-2 rounded-lg border border-border"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              data-oid="dt6ms09"
            >
              <div className="flex items-center gap-2 mb-1" data-oid="rlik7r-">
                <Globe className="h-4 w-4 text-indigo-500" data-oid="vmf9moq" />
                <span className="text-xs font-medium" data-oid="4sidfts">
                  Global Sync
                </span>
              </div>
              <div
                className="relative h-1.5 bg-indigo-500/20 rounded-full overflow-hidden"
                data-oid=".hfwut1"
              >
                <motion.div
                  className="absolute inset-y-0 left-0 bg-indigo-500"
                  style={{ width: `${syncStatus}%` }}
                  data-oid="52v6:u0"
                />
              </div>
            </motion.div>
          </div>

          {/* Right column */}
          <div className="space-y-3" data-oid="d-o36zb">
            {/* Notifications */}
            <motion.div
              className="bg-card/30 backdrop-blur-sm p-2 rounded-lg border border-border"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              data-oid="0je.fhq"
            >
              <div className="flex items-center gap-2 mb-1" data-oid="exx0:xs">
                <Bell className="h-4 w-4 text-pink-500" data-oid="fpop790" />
                <span className="text-xs font-medium" data-oid="j70k782">
                  Notifications
                </span>
              </div>
              <div className="flex items-center gap-2" data-oid="mcu-sl_">
                <motion.div
                  key={notifications}
                  className="bg-pink-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center"
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring" }}
                  data-oid="b:v7n9d"
                >
                  {notifications}
                </motion.div>
                <span
                  className="text-xs text-muted-foreground"
                  data-oid="m75x959"
                >
                  New
                </span>
              </div>
            </motion.div>

            {/* Data Transfer */}
            <motion.div
              className="bg-card/30 backdrop-blur-sm p-2 rounded-lg border border-border"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              data-oid="9sqp7jq"
            >
              <div className="flex items-center gap-2 mb-1" data-oid="97ndvqb">
                <ArrowUpDown
                  className="h-4 w-4 text-emerald-500"
                  data-oid="snf_bqy"
                />
                <span className="text-xs font-medium" data-oid=":-uxqib">
                  Data Transfer
                </span>
              </div>
              <div className="flex items-center gap-1" data-oid="wrayin3">
                <Zap className="h-3 w-3 text-emerald-500" data-oid="09kqn1m" />
                <span
                  className="text-xs text-muted-foreground"
                  data-oid="x06vvz8"
                >
                  2.5 MB/s
                </span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealTimeUpdates;
